import Modal from "@/components/atoms/Modal";
import Select from "@/components/atoms/Select";
import {
  INVALID_FORM_MESSAGE,
  INVALID_MAXIMUM_CARACTERE_MESSAGE,
  INVALID_MINIMUM_CARACTERE_MESSAGE,
  INVALID_NAME_MESSAGE,
} from "@/constants/validatorMessages";
import { TeamForm } from "@/interfaces/Team";
import { IModalMethods } from "@/interfaces/Modal";
import { useGetUsersQuery } from "@/services/users";
import { useStoreTeamMutation } from "@/services/teams";
import { FetchBaseQueryError } from "@reduxjs/toolkit/dist/query";
import {
  ElementRef,
  ForwardRefRenderFunction,
  forwardRef,
  useImperativeHandle,
  useMemo,
  useRef,
} from "react";
import { Col, Form, Row } from "react-bootstrap";
import {
  Controller,
  SubmitErrorHandler,
  SubmitHandler,
  useForm,
} from "react-hook-form";
import { ToastContentProps, toast } from "react-toastify";

const StoreTeamModal: ForwardRefRenderFunction<IModalMethods> = (_, ref) => {
  const ModalRef = useRef<ElementRef<typeof Modal>>(null);
  const [storeTeamAsync] = useStoreTeamMutation();
  const { data: usersData } = useGetUsersQuery({
    pageIndex: 0,
    pageSize: 1000,
    sortBy: "firstname",
    sortDirection: "asc",
  });

  const userOptions = useMemo(
    () =>
      usersData?.data?.map((user) => ({
        value: user.id,
        label: `${user.firstname} ${user.lastname} (${user.email})`,
      })) || [],
    [usersData],
  );

  const {
    control,
    handleSubmit,
    register,
    reset,
    formState: { errors },
  } = useForm<TeamForm>({
    defaultValues: { managers: [], fullVisibility: false },
  });

  const showOrHiddenModal = () => ModalRef.current?.showOrHiddenModal();

  useImperativeHandle(ref, () => ({
    showOrHiddenModal,
  }));

  const onSubmit: SubmitHandler<TeamForm> = ({
    managers,
    description,
    color,
    fullVisibility,
    ...data
  }) => {
    if (!managers || managers.length === 0) {
      toast.error(INVALID_FORM_MESSAGE);
      return;
    }
    toast.promise(
      storeTeamAsync({
        ...data,
        description: description || null,
        color: color || null,
        fullVisibility: fullVisibility ?? false,
        managerIds: managers.map((m) => m.value),
      }).unwrap(),
      {
        pending: "Création de l'équipe en cours...",
        success: {
          render() {
            reset();
            showOrHiddenModal();
            return (
              <p style={{ marginBottom: 0, textAlign: "center" }}>
                L&apos;équipe a bien été créée
              </p>
            );
          },
        },
        error: {
          render({ data }: ToastContentProps<FetchBaseQueryError>) {
            if (data?.status === 422) {
              return "Les champs que vous avez remplis semblent être incorrects.";
            }
            return "Une erreur est survenue";
          },
        },
      },
    );
  };

  const onError: SubmitErrorHandler<TeamForm> = () =>
    toast.error(INVALID_FORM_MESSAGE);

  return (
    <Modal
      ref={ModalRef}
      title="Créer une équipe"
      requiredLabel
      onSubmit={handleSubmit(onSubmit, onError)}
    >
      <Row className="gx-3">
        <Col lg={6}>
          <Form.Group className="mb-3">
            <Form.Label>
              Nom <span className="text-danger">*</span> :
            </Form.Label>
            <Form.Control
              placeholder="ex : Équipe Nord"
              type="text"
              {...register("name", {
                required: INVALID_NAME_MESSAGE,
                minLength: {
                  value: 2,
                  message: INVALID_MINIMUM_CARACTERE_MESSAGE,
                },
                maxLength: {
                  value: 255,
                  message: INVALID_MAXIMUM_CARACTERE_MESSAGE,
                },
              })}
              isInvalid={!!errors.name}
            />
            <Form.Control.Feedback type="invalid">
              {errors.name?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col lg={6}>
          <Form.Group className="mb-3">
            <Form.Label>Couleur :</Form.Label>
            <Form.Control
              type="color"
              {...register("color")}
              isInvalid={!!errors.color}
            />
            <Form.Control.Feedback type="invalid">
              {errors.color?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col lg={12}>
          <Form.Group className="mb-3">
            <Form.Label>Description :</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              {...register("description", {
                maxLength: {
                  value: 2000,
                  message: INVALID_MAXIMUM_CARACTERE_MESSAGE,
                },
              })}
              isInvalid={!!errors.description}
            />
            <Form.Control.Feedback type="invalid">
              {errors.description?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col lg={12}>
          <Form.Group className="mb-3">
            <Form.Label>
              Managers <span className="text-danger">*</span> :
            </Form.Label>
            <Controller
              control={control}
              name="managers"
              rules={{
                validate: (v) =>
                  (v && v.length > 0) || INVALID_FORM_MESSAGE,
              }}
              render={({ field }) => (
                <Select
                  isMulti
                  closeMenuOnSelect={false}
                  options={userOptions}
                  value={field.value ?? []}
                  onChange={(v) => field.onChange(v)}
                />
              )}
            />
            <Form.Control.Feedback type="invalid">
              {errors.managers?.message as string | undefined}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col lg={12}>
          <Form.Group className="mb-3">
            <Form.Check
              type="switch"
              id="store-full-visibility"
              label="Autoriser le chef d'équipe à voir toutes les fiches"
              {...register("fullVisibility")}
            />
          </Form.Group>
        </Col>
      </Row>
    </Modal>
  );
};

export default forwardRef(StoreTeamModal);
